import { useCallback, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';

export type CarouselDirection = 'previous' | 'next';

/** The copies a looping row draws. The middle one is the real one; the other two are only for scrolling. */
export const LOOP_COPIES = [0, 1, 2] as const;
export const REAL_COPY = 1;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function trackGap(viewport: HTMLElement) {
  const track = viewport.firstElementChild;
  return track ? parseFloat(getComputedStyle(track).columnGap) || 0 : 0;
}

/** How far one item moves the row: the first item's width plus the gap after it. */
function itemStep(viewport: HTMLElement) {
  const firstItem = viewport.firstElementChild?.firstElementChild;
  if (!firstItem) {
    return 0;
  }
  return firstItem.getBoundingClientRect().width + trackGap(viewport);
}

/**
 * Endless sideways scrolling for a row of `itemCount` items, as in the design's prototype. When the items
 * do not all fit, the row draws them three times and sits on the middle copy. An arrow or arrow key moves
 * one item, and after any scroll the row jumps without animation to the same item in the middle copy, so
 * there is always more to scroll either way. When every item fits, `isLooping` is false and the row draws
 * one copy and no arrows. An item is a card, or a column of cards in the model row.
 */
export function useCarousel(itemCount: number, isEnabled = true) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isLooping, setIsLooping] = useState(false);
  /** The item the row is on, kept while the row switches between one copy and three. */
  const currentItem = useRef(0);

  const looping = isLooping && isEnabled;

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !isEnabled || itemCount === 0) {
      return;
    }

    const measure = () => {
      const step = itemStep(viewport);
      if (!step) {
        return;
      }
      // The last item that fits has no gap after it, so the gap is added back before dividing by steps.
      const visibleItems = Math.max(1, Math.floor((viewport.clientWidth + trackGap(viewport) + 1) / step));
      const shouldLoop = itemCount > visibleItems;
      if (shouldLoop !== isLooping) {
        setIsLooping(shouldLoop);
        return;
      }
      viewport.scrollTo({ left: shouldLoop ? (itemCount + currentItem.current) * step : 0, behavior: 'instant' });
    };

    // Back to the middle copy, on the same item. Runs once a scroll has settled.
    const recenter = () => {
      const step = itemStep(viewport);
      if (!isLooping || !step) {
        return;
      }
      const index = Math.round(viewport.scrollLeft / step);
      currentItem.current = ((index % itemCount) + itemCount) % itemCount;
      const middleIndex = itemCount + currentItem.current;
      if (index !== middleIndex) {
        viewport.scrollTo({ left: middleIndex * step, behavior: 'instant' });
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);

    // Safari has no `scrollend`, so it settles on a short pause in scroll events instead.
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(recenter, 150);
    };
    const hasScrollEnd = 'onscrollend' in viewport;
    viewport.addEventListener(hasScrollEnd ? 'scrollend' : 'scroll', hasScrollEnd ? recenter : onScroll);

    return () => {
      observer.disconnect();
      clearTimeout(settleTimer);
      viewport.removeEventListener(hasScrollEnd ? 'scrollend' : 'scroll', hasScrollEnd ? recenter : onScroll);
    };
  }, [itemCount, isLooping, isEnabled]);

  const move = useCallback(
    (direction: CarouselDirection) => {
      const viewport = viewportRef.current;
      const step = viewport ? itemStep(viewport) : 0;
      if (!viewport || !step || !looping) {
        return;
      }
      const index = Math.round(viewport.scrollLeft / step) + (direction === 'next' ? 1 : -1);
      viewport.scrollTo({ left: index * step, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    },
    [looping],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? 'previous' : 'next');
      }
    },
    [move],
  );

  return { viewportRef, isLooping: looping, move, onKeyDown };
}
